import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'
import ModalContext from 'contexts/ModalContext'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import WaitingListRequestFormModal from '../WaitingListRequest/WaitingListRequestFormModal'
import PrivateRequestFormModal from '../PrivateRequest/PrivateRequestFormModal'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Plural from 'components/utils/Formatters/Plural'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import TourV2DepartureTileDates from './TourV2DepartureTileDates'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import { isTourLimitedTimeOffer } from 'lib/tours/tourUtils'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

const Tile = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: grid;
  padding: ${rem(20)};
  gap: ${rem(16)};
  grid-template-areas:
  "dates"
  "reason"
  "call"
  "cta";

  ${mediaQueryUp.tablet} {
    column-gap: ${rem(40)};
    row-gap: ${rem(12)};
    padding: ${rem(16)} 0;
    grid-template-columns: 1fr min-content;
    grid-template-areas:
    "dates cta"
    "reason cta"
    "call cta";
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(320)} 1fr min-content;
    grid-template-areas:
    "dates reason cta"
    "dates call cta";
  }

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: ${rem(320)} ${rem(180)} ${rem(200)} min-content;
    grid-template-areas: "dates reason call cta";
  }
`

const ReasonGridArea = styled.div`
  grid-area: reason;

  /* We collapsed the columns, now we need to push them together as they would be centered and too far apart */
  ${mediaQueryOnly.desktop} {
    align-self: start;
  }
`

const CallGridArea = styled.div`
  grid-area: call;

  /* We collapsed the columns, now we need to push them together as they would be centered and too far apart */
  ${mediaQueryOnly.desktop} {
    align-self: end;
  }
`

interface Props {
  offer: App.Tours.TourV2Offer
  departure: App.Tours.TourV2OfferDeparture
  travellers: Array<OfferPageRoom>
}

function TourV2DepartureSoldOutTile(props: Props) {
  const { departure, offer, travellers } = props

  const intl = useIntl()
  const contact = useContext(ContactContext)
  const showModal = useContext(ModalContext)
  const onJoinWaitlist = useCallback(() => {
    showModal(<WaitingListRequestFormModal offer={offer} departure={departure} />)
  }, [departure, offer, showModal])

  const onRequestPrivateDeparture = useCallback(() => {
    showModal(<PrivateRequestFormModal
      offer={offer}
      selectedDeparture={departure}
      defaultTravellers={travellers}
    />)
  }, [departure, travellers, offer, showModal])

  const travellerCount = useMemo(() => sumUpOccupancies(travellers), [travellers])
  const reason = departure.options.length === 0 ? 'sold-out' : 'travellers'

  return <Tile>
    <TourV2DepartureTileDates departure={departure} gridArea="dates" />
    <ReasonGridArea>
      {/* TODO: i18n - contains leading/trailing spaces */}
      {reason === 'travellers' && <CaptionTextBlock
        variant="medium"
        colour="urgency"
        weight="bold"
        startIcon={<SolidExclamationCircleIcon colour="urgency" />}
      >
        Sold out for <Plural count={travellerCount} singular="traveller" withCount />
      </CaptionTextBlock>}
      {reason === 'sold-out' && <CaptionTextBlock
        variant="medium"
        colour="urgency"
        weight="bold"
        startIcon={<SolidExclamationCircleIcon colour="urgency" />}
      >
        {intl.formatMessage({
          id: '4a6ygqYNQd',
          defaultMessage: 'Sold out!',
          description: 'Caption indicating departure is sold out',
        })}
      </CaptionTextBlock>}
    </ReasonGridArea>
    <CallGridArea>
      <BodyText variant="medium">
        <FormattedMessage
          id="+SCfCuLzAe"
          defaultMessage="Give our team a call on <phoneLink>{phoneNumber}</phoneLink> for more tour options"
          description="Text prompting user to call for more tour options when departure is sold out"
          values={{
            phoneNumber: contact.defaultContact.humanReadable,
            phoneLink: (chunks) => <TextLink wrap="no-wrap" href={`tel:${contact.defaultContact.number}`}>{chunks}</TextLink>,
          }}
        />
      </BodyText>
    </CallGridArea>
    <Group
      direction="horizontal"
      gap={8}
      tabletHorizontalAlign="end"
      horizontalAlign="stretch"
      verticalAlign="center"
      style={{ gridArea: 'cta' }}
    >
      {isSalesforceTourV2(offer) && !isTourLimitedTimeOffer(offer) && <TextButton kind="tertiary" onClick={onRequestPrivateDeparture}>{intl.formatMessage({
        id: 'WqjYSTxnzE',
        defaultMessage: 'Request a private departure',
        description: 'Button text to request a private tour departure',
      })}
      </TextButton>}
      <TextButton kind="secondary" onClick={onJoinWaitlist}>{intl.formatMessage({
        id: '2ym+Pj1g6q',
        defaultMessage: 'Join waitlist',
        description: 'Button text to join tour departure waitlist',
      })}
      </TextButton>
    </Group>
  </Tile>
}

export default React.memo(TourV2DepartureSoldOutTile)
